(function (global, factory) {
  typeof exports === 'object' && typeof module !== 'undefined' 
    ? module.exports = factory() 
    : typeof define === 'function' && define.amd 
      ? define(factory) 
      : (global.ImageComparisonSlider = factory());
}(this, (function () {
  'use strict';
  
  /**
 * 工具函数
 */

/**
 * 节流函数
 * @param {Function} func - 要执行的函数
 * @param {number} limit - 时间限制(毫秒)
 * @returns {Function} 节流后的函数
 */
function throttle(func, limit = 100) {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

/**
 * 预加载图片
 * @param {Array|string} urls - 图片URL或URL数组
 * @returns {Promise} 加载完成的Promise
 */
function preloadImages(urls) {
  const imageUrls = Array.isArray(urls) ? urls : [urls];
  
  const promises = imageUrls.map(url => {
    return new Promise((resolve, reject) => {
      if (!url) {
        resolve();
        return;
      }
      
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
      img.src = url;
    });
  });
  
  return Promise.all(promises);
}

/**
 * 验证选项
 * @param {Object} options - 用户选项
 * @returns {Object} 验证后的选项
 */
function validateOptions(options) {
  const validated = { ...options };
  
  // 验证初始位置
  if (typeof validated.initialPosition !== 'number' || 
      validated.initialPosition < 0 || 
      validated.initialPosition > 100) {
    console.warn('initialPosition 必须是 0-100 之间的数字，使用默认值 50');
    validated.initialPosition = 50;
  }
  
  // 验证图片URL
  if (validated.beforeImage && typeof validated.beforeImage !== 'string') {
    console.warn('beforeImage 必须是字符串 URL');
    validated.beforeImage = null;
  }
  
  if (validated.afterImage && typeof validated.afterImage !== 'string') {
    console.warn('afterImage 必须是字符串 URL');
    validated.afterImage = null;
  }
  
  // 验证图片元素
  if (validated.beforeElement && !(validated.beforeElement instanceof HTMLElement)) {
    console.warn('beforeElement 必须是 HTMLElement');
    validated.beforeElement = null;
  }
  
  if (validated.afterElement && !(validated.afterElement instanceof HTMLElement)) {
    console.warn('afterElement 必须是 HTMLElement');
    validated.afterElement = null;
  }
  
  // 验证回调函数
  const callbackNames = ['onInit', 'onChange', 'onDragStart', 'onDragEnd', 'onReset'];
  callbackNames.forEach(name => {
    if (validated[name] && typeof validated[name] !== 'function') {
      console.warn(`${name} 必须是函数`);
      validated[name] = null;
    }
  });
  
  return validated;
}
/**
 * 样式管理
 */

// 创建全局样式表
let styleSheetAdded = false;

/**
 * 创建样式表
 */
function createStylesheet() {
  if (styleSheetAdded || typeof document === 'undefined') return;
  
  const style = document.createElement('style');
  style.id = 'image-comparison-slider-styles';
  style.textContent = `
    .image-comparison-container {
      position: relative;
      width: 100%;
      height: auto;
      border-radius: 8px;
      overflow: hidden;
      -webkit-touch-callout: none;
      -webkit-user-select: none;
      user-select: none;
    }
    
    .image-comparison-container .image-container {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      overflow: hidden;
    }
    
    .image-comparison-container .image-before,
    .image-comparison-container .image-after {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      user-select: none;
      pointer-events: none;
      backface-visibility: hidden;
      -webkit-backface-visibility: hidden;
    }
    
    .image-comparison-container .image-after-container {
      will-change: clip-path;
    }
    
    .image-comparison-container .divider-line {
      position: absolute;
      top: 0;
      height: 100%;
      z-index: 10;
      cursor: col-resize;
      transition: background-color 0.2s;
      will-change: left;
    }
    
    .image-comparison-container .divider-handle {
      position: absolute;
      top: 50%;
      transform: translate(-50%, -50%);
      border-radius: 50%;
      display: flex;
      justify-content: center;
      align-items: center;
      color: white;
      box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
      cursor: pointer;
      z-index: 11;
      transition: transform 0.3s ease, background-color 0.2s ease;
      border: 2px solid white;
      will-change: left, transform;
    }
    
    .image-comparison-container .divider-handle .handle-dot {
      width: 8px;
      height: 8px;
      background-color: white;
      border-radius: 50%;
    }
    
    .image-comparison-container .labels {
      position: absolute;
      top: 20px;
      width: 100%;
      display: flex;
      justify-content: space-between;
      padding: 0 20px;
      z-index: 5;
      pointer-events: none;
    }
    
    .image-comparison-container .label {
      background-color: rgba(0, 0, 0, 0.7);
      padding: 10px 20px;
      border-radius: 30px;
      font-weight: bold;
      backdrop-filter: blur(5px);
    }
    
    .image-comparison-container .label-before {
      color: #4cc9f0;
    }
    
    .image-comparison-container .label-after {
      color: #f72585;
    }
    
    @media (max-width: 480px) {
      
      .image-comparison-container .label {
        font-size: 0.9rem;
        padding: 8px 15px;
      }
    }
  `;
  
  document.head.appendChild(style);
  styleSheetAdded = true;
}

/**
 * 应用动态样式
 * @param {Object} elements - DOM元素
 * @param {Object} options - 配置选项
 */
function applyStyles(elements, options) {
  if (!elements.dividerLine || !elements.dividerHandle) return;
  
  // 设置分割线样式
  elements.dividerLine.style.width = `${options.lineWidth}px`;
  elements.dividerLine.style.backgroundColor = options.lineColor;
  elements.dividerLine.style.boxShadow = `0 0 15px ${options.lineColor}99`;
  
  // 设置手柄样式
  elements.dividerHandle.style.width = `${options.handleSize}px`;
  elements.dividerHandle.style.height = `${options.handleSize}px`;
  elements.dividerHandle.style.backgroundColor = options.handleColor;
  elements.dividerHandle.style.boxShadow = `0 5px 15px ${options.handleColor}66`;
}
  
  /**
 * 事件处理
 */
/**
 * 设置事件监听器
 * @param {ImageComparisonSlider} instance - 实例
 * @returns {Object} 事件监听器对象
 */
function setupEventListeners(instance) {
  const { container, elements, options } = instance;
  
  // 创建节流函数
  const throttledDrag = throttle((clientX) => {
    instance.handleDrag(clientX);
  }, options.smoothDrag ? 10 : 0);
  
  // 事件处理函数
  const onMouseDown = (e) => {
    e.preventDefault();
    instance.startDragging();
  };
  
  const onTouchStart = (e) => {
    e.preventDefault();
    instance.startDragging();
  };
  
  const onMouseMove = (e) => {
    throttledDrag(e.clientX);
  };
  
  const onTouchMove = (e) => {
    if (e.touches.length === 1) {
      e.preventDefault();
      throttledDrag(e.touches[0].clientX);
    }
  };
  
  const onMouseUp = () => {
    instance.stopDragging();
  };
  
  const onTouchEnd = () => {
    instance.stopDragging();
  };
  
  const onClick = (e) => {
    const clientX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
    instance.handleClick(clientX);
  };
  
  const onKeyDown = (e) => {
    if (!options.keyboardControl) return;
    
    let newPosition = instance.state.position;
    
    if (e.key === 'ArrowLeft') {
      newPosition = Math.max(0, newPosition - 1);
      instance.updatePosition(newPosition, false);
      e.preventDefault();
    } else if (e.key === 'ArrowRight') {
      newPosition = Math.min(100, newPosition + 1);
      instance.updatePosition(newPosition, false);
      e.preventDefault();
    } else if (e.key === 'Home') {
      instance.setPosition(0);
      e.preventDefault();
    } else if (e.key === 'End') {
      instance.setPosition(100);
      e.preventDefault();
    } else if (e.key === 'r' || e.key === 'R') {
      instance.reset();
      e.preventDefault();
    }
  };
  
  // 添加事件监听器
  elements.dividerHandle.addEventListener('mousedown', onMouseDown);
  elements.dividerHandle.addEventListener('touchstart', onTouchStart, { passive: false });
  elements.dividerLine.addEventListener('mousedown', onMouseDown);
  elements.dividerLine.addEventListener('touchstart', onTouchStart, { passive: false });
  
  if (options.clickToMove) {
    container.addEventListener('click', onClick);
    container.addEventListener('touchstart', onClick, { passive: false });
  }
  
  document.addEventListener('mousemove', onMouseMove);
  document.addEventListener('touchmove', onTouchMove, { passive: false });
  document.addEventListener('mouseup', onMouseUp);
  document.addEventListener('touchend', onTouchEnd);
  
  if (options.keyboardControl) {
    document.addEventListener('keydown', onKeyDown);
  }
  
  // 返回移除函数
  return {
    remove: () => {
      elements.dividerHandle.removeEventListener('mousedown', onMouseDown);
      elements.dividerHandle.removeEventListener('touchstart', onTouchStart);
      elements.dividerLine.removeEventListener('mousedown', onMouseDown);
      elements.dividerLine.removeEventListener('touchstart', onTouchStart);
      
      if (options.clickToMove) {
        container.removeEventListener('click', onClick);
        container.removeEventListener('touchstart', onClick);
      }
      
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('touchend', onTouchEnd);
      
      if (options.keyboardControl) {
        document.removeEventListener('keydown', onKeyDown);
      }
    }
  };
}

/**
 * 图片对比滑块主类
 */
class ImageComparisonSlider {
  /**
   * 构造函数
   * @param {HTMLElement|string} container - 容器元素或选择器
   * @param {Object} options - 配置选项
   */
  constructor(container, options = {}) {
    // 验证和合并选项
    this.options = this.mergeOptions(options);
    
    // 获取容器元素
    this.container = this.getContainerElement(container);
    
    // 验证容器
    if (!this.container) {
      throw new Error('无效的容器元素');
    }
    
    // 初始化状态
    this.state = {
      position: this.options.initialPosition,
      isDragging: false,
      isInitialized: false,
      animationFrameId: null
    };
    
    // 存储DOM引用
    this.elements = {};
    
    // 初始化
    this.init();
  }
  
  /**
   * 合并默认选项和用户选项
   * @param {Object} userOptions - 用户选项
   * @returns {Object} 合并后的选项
   */
  mergeOptions(userOptions) {
    const defaultOptions = {
      // 初始位置 (0-100)
      initialPosition: 50,
      
      // 图片URL
      beforeImage: null,
      afterImage: null,
      
      // 图片元素 (如果不提供URL)
      beforeElement: null,
      afterElement: null,
      
      // 标签文本
      beforeLabel: 'Before',
      afterLabel: 'After',
      
      // 标签显示
      showLabels: true,
      
      // 手柄样式
      handleSize: 50,
      handleColor: 'rgba(67, 97, 238, 0.8)',
      handleHoverColor: 'rgba(76, 201, 240, 0.9)',
      
      // 线条样式
      lineWidth: 4,
      lineColor: 'rgba(255, 255, 255, 0.9)',
      lineHoverColor: '#4cc9f0',
      
      // 交互选项
      clickToMove: true,
      keyboardControl: true,
      
      // 动画选项
      animationDuration: 300,
      smoothDrag: true,
      
      // 容器宽高比
      aspectRatio: null,
      useImageAspectRatio: true,
      
      // 回调函数
      onInit: null,
      onChange: null,
      onDragStart: null,
      onDragEnd: null,
      onReset: null
    };
    
    return { ...defaultOptions, ...validateOptions(userOptions) };
  }
  
  /**
   * 获取容器元素
   * @param {HTMLElement|string} container - 容器元素或选择器
   * @returns {HTMLElement|null} 容器元素
   */
  getContainerElement(container) {
    if (typeof container === 'string') {
      return document.querySelector(container);
    } else if (container instanceof HTMLElement) {
      return container;
    }
    return null;
  }
  
  /**
   * 初始化
   */
  async init() {
    // 防止重复初始化
    if (this.state.isInitialized) {
      console.warn('已经初始化过了');
      return;
    }
    
    try {
      const loadedImages = await this.preloadImages();
      
      // 创建DOM结构
      this.createDOM();
      
      // 应用样式
      applyStyles(this.elements, this.options);
      
      this.applyContainerAspectRatio(loadedImages);
      
      // 设置事件监听器
      this.eventListeners = setupEventListeners(this);
      
      // 更新初始位置
      this.updatePosition(this.state.position);
      
      // 标记为已初始化
      this.state.isInitialized = true;
      
      // 调用初始化回调
      if (typeof this.options.onInit === 'function') {
        this.options.onInit(this);
      }
      
      console.log('图片对比滑块初始化完成');
    } catch (error) {
      console.error('初始化失败:', error);
      throw error;
    }
  }
  
  /**
   * 预加载图片
   */
  async preloadImages() {
    const images = [];
    
    if (this.options.beforeImage) {
      images.push(this.options.beforeImage);
    }
    
    if (this.options.afterImage) {
      images.push(this.options.afterImage);
    }
    
    if (images.length > 0) {
      return await preloadImages(images);
    }
    return [];
  }
  
  /**
   * 创建DOM结构
   */
  createDOM() {
    // 清空容器
    this.container.innerHTML = '';
    
    // 添加容器类名
    this.container.classList.add('image-comparison-container');
    
    // 创建图片容器
    const beforeContainer = document.createElement('div');
    beforeContainer.className = 'image-container image-before-container';
    
    const afterContainer = document.createElement('div');
    afterContainer.className = 'image-container image-after-container';
    
    // 创建图片元素
    let beforeImg, afterImg;
    
    if (this.options.beforeElement) {
      beforeImg = this.options.beforeElement;
      beforeImg.classList.add('image-before');
    } else {
      beforeImg = document.createElement('img');
      beforeImg.className = 'image-before';
      beforeImg.src = this.options.beforeImage || 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect width="100" height="100" fill="%23f0f0f0"/></svg>';
      beforeImg.alt = 'Before image';
    }
    
    if (this.options.afterElement) {
      afterImg = this.options.afterElement;
      afterImg.classList.add('image-after');
    } else {
      afterImg = document.createElement('img');
      afterImg.className = 'image-after';
      afterImg.src = this.options.afterImage || 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect width="100" height="100" fill="%23e0e0e0"/></svg>';
      afterImg.alt = 'After image';
    }
    
    beforeContainer.appendChild(beforeImg);
    afterContainer.appendChild(afterImg);
    
    // 创建分割线
    const dividerLine = document.createElement('div');
    dividerLine.className = 'divider-line';
    
    // 创建手柄
    const dividerHandle = document.createElement('div');
    dividerHandle.className = 'divider-handle';
    
    const handleDot = document.createElement('div');
    handleDot.className = 'handle-dot';
    dividerHandle.appendChild(handleDot);
    
    // 创建标签
    let labelsContainer = null;
    if (this.options.showLabels) {
      labelsContainer = document.createElement('div');
      labelsContainer.className = 'labels';
      
      const beforeLabel = document.createElement('div');
      beforeLabel.className = 'label label-before';
      beforeLabel.textContent = this.options.beforeLabel;
      
      const afterLabel = document.createElement('div');
      afterLabel.className = 'label label-after';
      afterLabel.textContent = this.options.afterLabel;
      
      labelsContainer.appendChild(beforeLabel);
      labelsContainer.appendChild(afterLabel);
    }
    
    // 将元素添加到容器
    this.container.appendChild(beforeContainer);
    this.container.appendChild(afterContainer);
    this.container.appendChild(dividerLine);
    this.container.appendChild(dividerHandle);
    
    if (labelsContainer) {
      this.container.appendChild(labelsContainer);
    }
    
    // 存储DOM引用
    this.elements = {
      container: this.container,
      beforeContainer,
      afterContainer,
      beforeImg,
      afterImg,
      dividerLine,
      dividerHandle,
      handleDot,
      labelsContainer
    };
  }
  
  applyContainerAspectRatio(loadedImages = []) {
    const arOpt = this.options.aspectRatio;
    let w = 0, h = 0;
    
    if (arOpt) {
      if (typeof arOpt === 'string') {
        const m = arOpt.replace(/\s+/g, '').match(/^(\d+(?:\.\d+)?)\/(\d+(?:\.\d+)?)$/);
        if (m) {
          w = parseFloat(m[1]);
          h = parseFloat(m[2]);
        }
      } else if (typeof arOpt === 'number') {
        w = arOpt;
        h = 1;
      } else if (typeof arOpt === 'object' && arOpt) {
        if (typeof arOpt.width === 'number' && typeof arOpt.height === 'number') {
          w = arOpt.width;
          h = arOpt.height;
        }
      }
    }
    
    if ((!w || !h) && this.options.useImageAspectRatio) {
      const imgs = Array.isArray(loadedImages) ? loadedImages.filter(Boolean) : [];
      let ref = null;
      if (this.elements.afterImg && this.elements.afterImg.naturalWidth > 0) {
        ref = this.elements.afterImg;
      } else if (this.elements.beforeImg && this.elements.beforeImg.naturalWidth > 0) {
        ref = this.elements.beforeImg;
      } else if (imgs.length > 0) {
        ref = imgs[imgs.length - 1];
      }
      if (ref && ref.naturalWidth > 0 && ref.naturalHeight > 0) {
        w = ref.naturalWidth;
        h = ref.naturalHeight;
      }
    }
    
    if (!w || !h) {
      w = 16; h = 9;
    }
    
    const ratioString = `${w} / ${h}`;
    if (typeof CSS !== 'undefined' && CSS.supports && CSS.supports('aspect-ratio', ratioString)) {
      this.elements.container.style.aspectRatio = ratioString;
      this.elements.container.style.height = 'auto';
      this.elements.container.style.paddingBottom = '';
    } else {
      const percent = (h / w) * 100;
      this.elements.container.style.height = '0';
      this.elements.container.style.paddingBottom = `${percent}%`;
    }
  }
  
  /**
   * 更新分割线位置
   * @param {number} position - 新位置 (0-100)
   * @param {boolean} animate - 是否使用动画
   */
  updatePosition(position, animate = false) {
    // 限制位置在0-100之间
    const newPosition = Math.max(0, Math.min(100, position));
    
    // 更新状态
    this.state.position = newPosition;
    
    // 使用requestAnimationFrame优化性能
    if (this.state.animationFrameId) {
      cancelAnimationFrame(this.state.animationFrameId);
    }
    
    this.state.animationFrameId = requestAnimationFrame(() => {
      // 更新after容器的裁剪区域
      this.elements.afterContainer.style.clipPath = 
        `polygon(0 0, ${newPosition}% 0, ${newPosition}% 100%, 0 100%)`;
      this.elements.afterContainer.style.webkitClipPath = 
        `polygon(0 0, ${newPosition}% 0, ${newPosition}% 100%, 0 100%)`;
      
      // 更新分割线位置
      this.elements.dividerLine.style.left = `${newPosition}%`;
      this.elements.dividerHandle.style.left = `${newPosition}%`;
      
      // 添加动画效果
      if (animate) {
        this.elements.dividerHandle.style.transition = 
          `left ${this.options.animationDuration}ms ease, transform ${this.options.animationDuration}ms ease`;
        this.elements.dividerLine.style.transition = 
          `left ${this.options.animationDuration}ms ease`;
        this.elements.afterContainer.style.transition = 
          `clip-path ${this.options.animationDuration}ms ease, -webkit-clip-path ${this.options.animationDuration}ms ease`;
        
        // 动画结束后移除过渡
        setTimeout(() => {
          this.elements.dividerHandle.style.transition = '';
          this.elements.dividerLine.style.transition = '';
          this.elements.afterContainer.style.transition = '';
        }, this.options.animationDuration);
      }
      
      // 调用变化回调
      if (typeof this.options.onChange === 'function') {
        this.options.onChange(newPosition, this);
      }
    });
  }
  
  /**
   * 设置位置 (带动画)
   * @param {number} position - 新位置 (0-100)
   */
  setPosition(position) {
    this.updatePosition(position, true);
  }
  
  /**
   * 获取当前位置
   * @returns {number} 当前位置
   */
  getPosition() {
    return this.state.position;
  }
  
  /**
   * 重置到中心
   */
  reset() {
    this.setPosition(50);
    
    // 添加手柄动画效果
    this.elements.dividerHandle.style.transform = 'translate(-50%, -50%) scale(1.2)';
    setTimeout(() => {
      this.elements.dividerHandle.style.transform = 'translate(-50%, -50%) scale(1)';
    }, 300);
    
    // 调用重置回调
    if (typeof this.options.onReset === 'function') {
      this.options.onReset(this);
    }
  }
  
  /**
   * 更新图片
   * @param {Object} images - 图片配置
   * @param {string} images.before - 左侧图片URL
   * @param {string} images.after - 右侧图片URL
   */
  updateImages(images = {}) {
    if (images.before) {
      this.elements.beforeImg.src = images.before;
    }
    
    if (images.after) {
      this.elements.afterImg.src = images.after;
    }
    
    // 重置位置
    this.reset();
    
    this.applyContainerAspectRatio();
  }
  
  /**
   * 更新标签
   * @param {Object} labels - 标签配置
   * @param {string} labels.before - 左侧标签
   * @param {string} labels.after - 右侧标签
   */
  updateLabels(labels = {}) {
    if (!this.options.showLabels || !this.elements.labelsContainer) {
      return;
    }
    
    const beforeLabel = this.elements.labelsContainer.querySelector('.label-before');
    const afterLabel = this.elements.labelsContainer.querySelector('.label-after');
    
    if (labels.before && beforeLabel) {
      beforeLabel.textContent = labels.before;
    }
    
    if (labels.after && afterLabel) {
      afterLabel.textContent = labels.after;
    }
  }
  
  /**
   * 开始拖拽
   */
  startDragging() {
    this.state.isDragging = true;
    
    // 更新手柄样式
    this.elements.dividerHandle.style.backgroundColor = this.options.handleHoverColor;
    this.elements.dividerLine.style.backgroundColor = this.options.lineHoverColor;
    try { this.elements.dividerHandle.classList.add('dragging'); } catch(_) {}
    
    // 调用拖拽开始回调
    if (typeof this.options.onDragStart === 'function') {
      this.options.onDragStart(this);
    }
  }
  
  /**
   * 停止拖拽
   */
  stopDragging() {
    this.state.isDragging = false;
    
    // 恢复手柄样式
    this.elements.dividerHandle.style.backgroundColor = this.options.handleColor;
    this.elements.dividerLine.style.backgroundColor = this.options.lineColor;
    try { this.elements.dividerHandle.classList.remove('dragging'); } catch(_) {}
    
    // 调用拖拽结束回调
    if (typeof this.options.onDragEnd === 'function') {
      this.options.onDragEnd(this);
    }
  }
  
  /**
   * 处理拖拽移动
   * @param {number} clientX - 鼠标/触摸的X坐标
   */
  handleDrag(clientX) {
    if (!this.state.isDragging) return;
    
    const containerRect = this.container.getBoundingClientRect();
    const newPosition = ((clientX - containerRect.left) / containerRect.width) * 100;
    
    this.updatePosition(newPosition, false);
  }
  
  /**
   * 处理点击
   * @param {number} clientX - 点击的X坐标
   */
  handleClick(clientX) {
    if (!this.options.clickToMove) return;
    
    const containerRect = this.container.getBoundingClientRect();
    const newPosition = ((clientX - containerRect.left) / containerRect.width) * 100;
    
    this.setPosition(newPosition);
  }
  
  /**
   * 销毁实例
   */
  destroy() {
    // 移除事件监听器
    if (this.eventListeners && typeof this.eventListeners.remove === 'function') {
      this.eventListeners.remove();
    }
    
    // 清除动画帧
    if (this.state.animationFrameId) {
      cancelAnimationFrame(this.state.animationFrameId);
    }
    
    // 清空容器
    this.container.innerHTML = '';
    this.container.classList.remove('image-comparison-container');
    
    // 重置状态
    this.state.isInitialized = false;
    this.elements = {};
    
    console.log('图片对比滑块已销毁');
  }
  
  /**
   * 静态方法：批量创建
   * @param {Array} configs - 配置数组
   * @returns {Array} 实例数组
   */
  static createAll(configs) {
    return configs.map(config => {
      const { container, options } = config;
      return new ImageComparisonSlider(container, options);
    });
  }
}

  
  // 创建全局样式
  if (typeof document !== 'undefined') {
    createStylesheet();
  }
  
  window.ImageComparisonSlider = ImageComparisonSlider;
  return ImageComparisonSlider;
})));
